
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import Select from "react-select";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ptBR } from "date-fns/locale";

import {
  FaBell,
  FaSearch,
  FaFilePdf,
  FaCheck,
  FaSyncAlt,
  FaRegClipboard,
} from "react-icons/fa";

import { RiFileExcel2Line } from "react-icons/ri";

import api from "../../../services/api";
import "./notificacao.scss";

// ============================================================
// CONFIGURAÇÃO DAS ROTAS
// ============================================================

const ENDPOINTS = {
  consultar: "/notificacao/tipo",
  veiculos: "/equipamentos",
  grupos: "/grupos1",
  pdf: "/notificacaoPDF",
};

// ============================================================
// TIPOS DE ALERTA
// A opção TODOS não aplica filtro por tipo.
// Confirme os valores com a coluna no.tipo do PostgreSQL.
// ============================================================

const TIPOS_ALERTA = [
  { value: "TODOS", label: "Todos" },
  { value: "ancora", label: "Âncora" },
  { value: "area", label: "Área" },
  { value: "bateria", label: "Bateria" },
  { value: "bateria (falha)", label: "Bateria (Falha)" },
  {
    value: "excesso de tempo parado",
    label: "Excesso de Tempo Parado",
  },
  {
    value: "parada de emergência",
    label: "Parada de Emergência",
  },
  { value: "ignicao", label: "Ignição" },
  { value: "rota", label: "Rota" },
  { value: "rpm", label: "RPM" },
  {
    value: "temperatura ambiente",
    label: "Temperatura Ambiente",
  },
  {
    value: "temperatura da unidade",
    label: "Temperatura da Unidade",
  },
  {
    value: "temperatura do motor",
    label: "Temperatura do Motor",
  },
  { value: "velocidade", label: "Velocidade" },
];
// ============================================================
// UTILITÁRIOS
// ============================================================

const obterHoje = () => {
  const data = new Date();

  return [
    data.getFullYear(),
    String(data.getMonth() + 1).padStart(2, "0"),
    String(data.getDate()).padStart(2, "0"),
  ].join("-");
};

const obterHeaders = () => ({
  typo: localStorage.getItem("id"),
  inova: localStorage.getItem("inova"),
});

const extrairLista = (data, chaves = []) => {
  if (Array.isArray(data)) return data;

  for (const chave of chaves) {
    if (Array.isArray(data?.[chave])) {
      return data[chave];
    }
  }

  return [];
};

const valorCampo = (objeto, ...campos) => {
  for (const campo of campos) {
    const valor = objeto?.[campo];

    if (valor !== null && valor !== undefined && valor !== "") {
      return valor;
    }
  }

  return "";
};

const texto = (valor) => {
  if (valor === null || valor === undefined || valor === "") {
    return "—";
  }

  return String(valor);
};

const formatarDataHora = (valor) => {
  if (!valor) return "-";

  const data = new Date(valor);

  if (Number.isNaN(data.getTime())) return "-";

  return data.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

// ============================================================
// ESTILOS DO REACT SELECT
// ============================================================

const selectStyles = {
  container: (base) => ({
    ...base,
    width: "100%",
    minWidth: 0,
  }),

  control: (base, state) => ({
    ...base,
    minHeight: 46,
    backgroundColor: "#101f2d",
    borderColor: state.isFocused ? "#08b8e8" : "#294253",
    borderRadius: 9,
    boxShadow: state.isFocused
      ? "0 0 0 3px rgba(8,184,232,.08)"
      : "none",
    cursor: "pointer",
    "&:hover": {
      borderColor: "#08b8e8",
    },
  }),

  valueContainer: (base) => ({
    ...base,
    padding: "5px 12px",
  }),

  singleValue: (base) => ({
    ...base,
    color: "#e5edf5",
    fontSize: 13,
  }),

  input: (base) => ({
    ...base,
    color: "#e5edf5",
  }),

  placeholder: (base) => ({
    ...base,
    color: "#91a8b8",
    fontSize: 13,
  }),

  menu: (base) => ({
    ...base,
    backgroundColor: "#102130",
    border: "1px solid #294253",
    borderRadius: 9,
    overflow: "hidden",
    zIndex: 9999,
  }),

  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),

  menuList: (base) => ({
    ...base,
    padding: 5,
    maxHeight: 260,
  }),

  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#087ca3"
      : state.isFocused
        ? "#1b3547"
        : "#102130",
    color: "#e5edf5",
    cursor: "pointer",
    padding: "11px 13px",
    fontSize: 13,
  }),

  indicatorSeparator: () => ({
    display: "none",
  }),

  dropdownIndicator: (base) => ({
    ...base,
    color: "#91a8b8",
    "&:hover": {
      color: "#08b8e8",
    },
  }),

  clearIndicator: (base) => ({
    ...base,
    color: "#91a8b8",
  }),
};

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================

export default function NotificacaoRelatorio() {
  // ----------------------------------------------------------
  // PERÍODO
  // ----------------------------------------------------------

  const [dataInicial, setDataInicial] = useState(obterHoje());
  const [horaInicial, setHoraInicial] = useState("00:00");

  const [dataFinal, setDataFinal] = useState(obterHoje());
  const [horaFinal, setHoraFinal] = useState("23:59");


  const [veiculosSelecionados, setVeiculosSelecionados] = useState([]);
  const [gruposSelecionados, setGruposSelecionados] = useState([]);


  // ----------------------------------------------------------
  // OPÇÕES CARREGADAS DA API
  // ----------------------------------------------------------

  const [veiculos, setVeiculos] = useState([]);
  const [grupos, setGrupos] = useState([]);

  const [carregandoVeiculos, setCarregandoVeiculos] = useState(false);
  const [carregandoGrupos, setCarregandoGrupos] = useState(false);

  const [erroVeiculos, setErroVeiculos] = useState(false);
  const [erroGrupos, setErroGrupos] = useState(false);

  // ----------------------------------------------------------
  // FILTROS SELECIONADOS
  // ----------------------------------------------------------

  const [tipoSelecionado, setTipoSelecionado] = useState(
    TIPOS_ALERTA[0]
  );

  const [veiculoSelecionado, setVeiculoSelecionado] = useState(null);
  const [grupoSelecionado, setGrupoSelecionado] = useState(null);

  // ----------------------------------------------------------
  // RESULTADOS
  // ----------------------------------------------------------

  const [registros, setRegistros] = useState([]);
  const [pesquisa, setPesquisa] = useState("");

  const [consultando, setConsultando] = useState(false);
  const [consultaRealizada, setConsultaRealizada] = useState(false);

  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(20);

  // ==========================================================
  // CARREGAR VEÍCULOS
  // GET /equipamentos
  // Headers: typo e inova
  // ==========================================================

  const carregarVeiculos = useCallback(async () => {
    setCarregandoVeiculos(true);
    setErroVeiculos(false);

    try {
      const { data } = await api.get(ENDPOINTS.veiculos, {
        headers: obterHeaders(),
      });

      const lista = extrairLista(data, [
        "equipamentos",
        "veiculos",
        "data",
        "rows",
      ]);

      //=====================================
      //OPÇÕES LISTA PRINCIPAL PLACA/VEICULO
      //=====================================
      const opcoes = lista
        .map((v) => {
          const placa = v.placa;
          const nome = v.nome;

          if (!placa) return null;

          return {
            value: String(placa),
            label: nome
              ? `${placa} `
              : String(placa),
            placa,
            nome,
            original: v,
          };
        })
        .filter(Boolean);

      setVeiculos(opcoes);

      console.log(
        `[NOTIFICAÇÕES] Veículos carregados: ${opcoes.length}`,
        opcoes
      );
    } catch (error) {
      console.error(
        "[NOTIFICAÇÕES] Erro ao carregar equipamentos:",
        error.response?.data || error
      );

      setErroVeiculos(true);
      setVeiculos([]);
    } finally {
      setCarregandoVeiculos(false);
    }
  }, []);

  // ==========================================================
  // CARREGAR GRUPOS
  // GET /grupos1
  // Headers: typo e inova
  // ==========================================================

  const carregarGrupos = useCallback(async () => {
    setCarregandoGrupos(true);
    setErroGrupos(false);

    try {
      const { data } = await api.get(ENDPOINTS.grupos, {
        headers: obterHeaders(),
      });

      const lista = extrairLista(data, [
        "grupos",
        "data",
        "rows",
      ]);

      const opcoes = lista
        .map((g) => {
          const id =
            g.id_grupo ??
            g.id ??
            g.grupo_id ??
            g.value;

          const nome =
            g.nome ??
            g.grupo ??
            g.descricao ??
            g.label;

          if (id === null || id === undefined || !nome) {
            return null;
          }

          return {
            value: String(id),
            label: String(nome),
            original: g,
          };
        })
        .filter(Boolean);

      setGrupos(opcoes);

      console.log(
        `[NOTIFICAÇÕES] Grupos carregados: ${opcoes.length}`,
        opcoes
      );
    } catch (error) {
      console.error(
        "[NOTIFICAÇÕES] Erro ao carregar grupos:",
        error.response?.data || error
      );

      setErroGrupos(true);
      setGrupos([]);
    } finally {
      setCarregandoGrupos(false);
    }
  }, []);

  // ==========================================================
  // CARREGAR OS FILTROS AO ABRIR A PÁGINA
  // ==========================================================

  useEffect(() => {
    carregarVeiculos();
    carregarGrupos();
  }, [carregarVeiculos, carregarGrupos]);

  // ==========================================================
  // ATUALIZAR VEÍCULOS E GRUPOS
  // ==========================================================

  const atualizarOpcoes = () => {
    carregarVeiculos();
    carregarGrupos();
  };

  // ==========================================================
  // MONTAR PARÂMETROS DA CONSULTA
  // ==========================================================

  const montarFiltros = useCallback(() => {
    const params = {
      dataInicial,
      dataFinal,
      horaInicial,
      horaFinal,
    };

    // "TODOS" não deve restringir os tipos no backend.
    if (
      tipoSelecionado &&
      tipoSelecionado.value !== "TODOS"
    ) {
      params.tipo = tipoSelecionado.value;
    }

    // A SQL original filtra a ocorrência por placa.
    if (veiculoSelecionado?.placa) {
      params.placa = veiculoSelecionado.placa;
    }

    // O backend precisa reconhecer este parâmetro e o
    // relacionamento com a tabela real de grupos.
    if (grupoSelecionado?.value) {
      params.grupo = grupoSelecionado.value;
    }

    return params;
  }, [
    dataInicial,
    dataFinal,
    horaInicial,
    horaFinal,
    tipoSelecionado,
    veiculoSelecionado,
    grupoSelecionado,
  ]);

  // ==========================================================
  // VALIDAR FILTROS
  // ==========================================================

  const validarFiltros = () => {
    if (!dataInicial || !dataFinal) {
      return "Informe a data inicial e a data final.";
    }

    const regexHora = /^([01]\d|2[0-3]):[0-5]\d$/;

    if (
      !regexHora.test(horaInicial) ||
      !regexHora.test(horaFinal)
    ) {
      return "Informe horários válidos.";
    }

    const inicio = new Date(`${dataInicial}T${horaInicial}:00`);
    const fim = new Date(`${dataFinal}T${horaFinal}:59`);

    if (
      Number.isNaN(inicio.getTime()) ||
      Number.isNaN(fim.getTime())
    ) {
      return "O período informado é inválido.";
    }

    if (inicio > fim) {
      return "O período inicial não pode ser posterior ao final.";
    }

    const temVeiculo =
      Boolean(veiculoSelecionado) ||
      veiculosSelecionados.length > 0;

    const temGrupo =
      Boolean(grupoSelecionado) ||
      gruposSelecionados.length > 0;

    if (!temVeiculo && !temGrupo) {
      Swal.fire({
        icon: "warning",
        title: "Verifique os filtros",
        text: "Selecione um veículo ou um grupo.",
      });

      return false;
    }

    return null;
  };

  // ==========================================================
  // CONSULTAR NOTIFICAÇÕES
  // GET /notificacao/tipo
  // ==========================================================

  const consultar = async () => {
    if (consultando) return;

    const erro = validarFiltros();

    if (erro) {
      await Swal.fire({
        icon: "warning",
        title: "Verifique os filtros",
        text: erro,
        background: "#0b1722",
        color: "#eaf2f8",
        confirmButtonColor: "#0891b2",
      });

      return;
    }

    setConsultando(true);
    setConsultaRealizada(false);
    setRegistros([]);
    setPagina(1);

    try {
      const params = montarFiltros();

      console.log("[NOTIFICAÇÕES] Parâmetros:", params);

      const { data } = await api.get(ENDPOINTS.consultar, {
        params,
        headers: obterHeaders(),
      });

      console.log("[NOTIFICAÇÕES] Resposta:", data);

      if (data?.ok === false) {
        throw new Error(
          data.erro || "Erro ao consultar as notificações."
        );
      }

      const lista = extrairLista(data, [
        "notificacoes",
        "dados",
        "rows",
        "data",
      ]);

      setRegistros(lista);
      setConsultaRealizada(true);

      if (!lista.length) {
        await Swal.fire({
          icon: "info",
          title: "Nenhuma notificação encontrada",
          text: "Não existem registros para os filtros selecionados.",
          background: "#0b1722",
          color: "#eaf2f8",
          confirmButtonColor: "#0891b2",
        });
      }
    } catch (error) {
      console.error(
        "[NOTIFICAÇÕES] Erro na consulta:",
        error.response?.data || error
      );

      await Swal.fire({
        icon: "error",
        title: "Falha na consulta",
        text:
          error.response?.data?.erro ||
          error.message ||
          "Não foi possível consultar as notificações.",
        background: "#0b1722",
        color: "#eaf2f8",
        confirmButtonColor: "#dc455a",
      });
    } finally {
      setConsultando(false);
    }
  };

  // ==========================================================
  // PESQUISA LOCAL
  // ==========================================================
  const registrosFiltrados = useMemo(() => {
    const termo = pesquisa.trim().toLocaleLowerCase("pt-BR");

    if (!termo) return registros;

    return registros.filter((item) => {
      const finalizado = Boolean(item.finalizado_em);

      const status = finalizado ? "Finalizado" : "Pendente";

      const campos = [
        item.id,
        item.notificacao_id,
        item.cliente_id,
        item.placa,
        item.descricao,
        item.motora,
        item.motorista,
        item.tipo,
        item.mensagem,
        item.endereco,
        item.endereco_id,
        item.finalizado_por,
        item.driver_id,
        status,
      ];

      return campos.some((valor) =>
        String(valor ?? "")
          .toLocaleLowerCase("pt-BR")
          .includes(termo)
      );
    });
  }, [registros, pesquisa]);

  // ==========================================================
  // PAGINAÇÃO
  // ==========================================================

  const totalPaginas = Math.max(
    1,
    Math.ceil(registrosFiltrados.length / porPagina)
  );

  const paginaAtual = Math.min(pagina, totalPaginas);

  const registrosPagina = useMemo(() => {
    const inicio = (paginaAtual - 1) * porPagina;

    return registrosFiltrados.slice(
      inicio,
      inicio + porPagina
    );
  }, [registrosFiltrados, paginaAtual, porPagina]);

  // ==========================================================
  // EXPORTAR EXCEL
  // ==========================================================

  const exportarExcel = async () => {
    if (!registrosFiltrados.length) {
      await Swal.fire({
        icon: "warning",
        title: "Nenhum registro",
        text: "Consulte os dados antes de exportar para Excel.",
        background: "#0b1722",
        color: "#eaf2f8",
        confirmButtonColor: "#f59e0b",
      });

      return;
    }

    const dados = registrosFiltrados.map((item) => ({
      Placa: item.placa ?? "",
      Descrição: item.descricao ?? "",
      "Ocorrido em": formatarDataHora(item.posicao_em),
      Motorista: item.motora ?? "",
      Tipo: item.tipo ?? "",
      Endereço: item.endereco ?? "",
      Notificação: item.mensagem ?? "",
      "Finalizado em": formatarDataHora(item.finalizado_em),
      "Finalizado por": item.finalizado_por ?? "",
      Comentário: item.obs ?? "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(dados);

    worksheet["!cols"] = [
      { wch: 12 },
      { wch: 24 },
      { wch: 22 },
      { wch: 24 },
      { wch: 25 },
      { wch: 45 },
      { wch: 40 },
      { wch: 22 },
      { wch: 24 },
      { wch: 36 },
    ];

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Notificações"
    );

    XLSX.writeFile(
      workbook,
      `Relatorio_Notificacoes_${obterHoje()}.xlsx`
    );
  };

  // ==========================================================
  // GERAR PDF PELO BACKEND PUPPETEER
  // ==========================================================

 
const gerarPDF = async () => {
  // =========================================================
  // VALIDAR REGISTROS
  // =========================================================

  if (!registrosFiltrados || registrosFiltrados.length === 0) {
    await Swal.fire({
      icon: "warning",
      title: "Nenhum registro",
      text: "Consulte as notificações antes de gerar o PDF.",
      background: "#0b1722",
      color: "#eaf2f8",
      confirmButtonColor: "#f59e0b",
    });

    return;
  }

  // =========================================================
  // NOTIFICAÇÕES QUE SERÃO ENVIADAS PARA O PDF
  // =========================================================

  const notificacoesPDF = registrosFiltrados;

  console.log("=================================");
  console.log("GERAÇÃO DE PDF - NOTIFICAÇÕES");
  console.log("Notificações:", notificacoesPDF);
  console.log("Quantidade:", notificacoesPDF.length);
  console.log("=================================");

  // =========================================================
  // CONFIRMAÇÃO
  // =========================================================

  const confirmacao = await Swal.fire({
    html: `
      <div class="notificacao-swal-pdf">
        <div class="notificacao-swal-pdf-icon">?</div>

        <div class="notificacao-swal-pdf-title">
          Gerar relatório PDF?
        </div>

        <p>Você está prestes a gerar um relatório com</p>

        <div class="notificacao-swal-pdf-count">
          ${notificacoesPDF.length}
        </div>

        <div class="notificacao-swal-pdf-label">
          notificação(ões)
        </div>
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: "Sim, gerar PDF",
    cancelButtonText: "Cancelar",
    reverseButtons: true,
    buttonsStyling: false,
    background: "#0b1722",
    color: "#eaf2f8",
    customClass: {
      popup: "notificacao-swal-popup",
      htmlContainer: "notificacao-swal-html",
      actions: "notificacao-swal-actions",
      confirmButton: "notificacao-swal-confirm",
      cancelButton: "notificacao-swal-cancel",
    },
  });

  if (!confirmacao.isConfirmed) {
    return;
  }

  // =========================================================
  // TOKEN
  // =========================================================

  const token = localStorage.getItem("id");

  if (!token) {
    await Swal.fire({
      icon: "error",
      title: "Cliente não identificado",
      text: "Não foi possível identificar o cliente.",
      background: "#0b1722",
      color: "#eaf2f8",
      confirmButtonColor: "#dc455a",
    });

    return;
  }

  // =========================================================
  // ABRIR NOVA ABA
  // =========================================================

  const novaAba = window.open("", "_blank");

  if (!novaAba) {
    await Swal.fire({
      icon: "warning",
      title: "Nova aba bloqueada",
      text: "Permita pop-ups para visualizar o relatório.",
      background: "#0b1722",
      color: "#eaf2f8",
      confirmButtonColor: "#f59e0b",
    });

    return;
  }

  // =========================================================
  // LOADING DENTRO DA NOVA ABA
  // =========================================================

  novaAba.document.write(`
    <!DOCTYPE html>
    <html lang="pt-BR">
      <head>
        <meta charset="UTF-8">
        <title>Gerando relatório...</title>

        <style>
          * {
            box-sizing: border-box;
          }

          html,
          body {
            margin: 0;
            min-height: 100%;
            font-family: Arial, Helvetica, sans-serif;
            background: #071722;
            color: #edf6fb;
          }

          body {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .card {
            width: min(390px, calc(100% - 32px));
            padding: 42px 30px;
            text-align: center;
            border: 1px solid #1d4051;
            border-radius: 18px;
            background: linear-gradient(145deg, #102635, #091a26);
            box-shadow: 0 25px 70px #0008;
          }

          .spinner {
            width: 46px;
            height: 46px;
            margin: 0 auto 24px;
            border: 3px solid #284250;
            border-top-color: #06b6d4;
            border-radius: 50%;
            animation: girar .8s linear infinite;
          }

          @keyframes girar {
            to {
              transform: rotate(360deg);
            }
          }

          h1 {
            font-size: 20px;
            margin: 0 0 10px;
          }

          p {
            color: #93aebe;
            font-size: 13px;
            line-height: 1.7;
          }

          .brand {
            margin-top: 25px;
            color: #4dbb8b;
            font-size: 10px;
            letter-spacing: 1.8px;
          }
        </style>
      </head>

      <body>
        <main class="card">
          <div class="spinner"></div>

          <h1>Gerando relatório</h1>

          <p>
            Estamos preparando seu PDF.<br>
            Isso pode levar alguns segundos.
          </p>

          <div class="brand">
            INOVA RASTREAMENTO
          </div>
        </main>
      </body>
    </html>
  `);

  novaAba.document.close();

  // =========================================================
  // FORM POST
  // =========================================================

  const form = novaAba.document.createElement("form");

  form.method = "POST";

  // Ajuste para o caminho real registrado no backend.
  form.action = `${api.defaults.baseURL}/notificacaoPDF`;

  form.style.display = "none";

  // =========================================================
  // TOKEN
  // =========================================================

  const inputToken = novaAba.document.createElement("input");

  inputToken.type = "hidden";
  inputToken.name = "token";
  inputToken.value = token;

  form.appendChild(inputToken);

  // =========================================================
  // NOTIFICAÇÕES
  // =========================================================

  const inputNotificacoes =
    novaAba.document.createElement("input");

  inputNotificacoes.type = "hidden";
  inputNotificacoes.name = "notificacoes";
  inputNotificacoes.value = JSON.stringify(notificacoesPDF);

  form.appendChild(inputNotificacoes);

  // =========================================================
  // QUANTIDADE
  // =========================================================

  const inputQuantidade =
    novaAba.document.createElement("input");

  inputQuantidade.type = "hidden";
  inputQuantidade.name = "quantidade";
  inputQuantidade.value = String(notificacoesPDF.length);

  form.appendChild(inputQuantidade);

  // =========================================================
  // DEBUG
  // =========================================================

  console.log("=================================");
  console.log("POST /notificacaoPDF");
  console.log("Token:", token);
  console.log("Quantidade:", notificacoesPDF.length);
  console.log("Notificações enviadas:", notificacoesPDF);
  console.log(
    "JSON:",
    JSON.stringify(notificacoesPDF)
  );
  console.log("=================================");

  // =========================================================
  // ENVIAR
  // =========================================================

  novaAba.document.body.appendChild(form);
  form.submit();

  // =========================================================
  // LOADING NO SISTEMA PRINCIPAL
  // =========================================================

  Swal.fire({
    html: `
      <div class="notificacao-swal-loading">
        <div class="notificacao-swal-spinner"></div>

        <div class="notificacao-swal-loading-title">
          Gerando PDF
        </div>

        <div class="notificacao-swal-loading-text">
          Preparando seu relatório...
        </div>
      </div>
    `,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    timer: 1100,
    background: "#0b1722",
    color: "#eaf2f8",
    customClass: {
      popup: "notificacao-swal-popup",
      htmlContainer: "notificacao-swal-html",
    },
  });
};


  // ==========================================================
  // LIMPAR FILTROS
  // ==========================================================

  const limparFiltros = () => {
    setDataInicial(obterHoje());
    setHoraInicial("00:00");
    setDataFinal(obterHoje());
    setHoraFinal("23:59");

    setTipoSelecionado(TIPOS_ALERTA[0]);

    setVeiculoSelecionado(null);
    setGrupoSelecionado(null);

    setPesquisa("");
    setRegistros([]);
    setConsultaRealizada(false);
    setPagina(1);
  };

  //=====================================
  //FUNÇÃO ABIRAR VEICULOS
  //======================================
  const abrirSelecaoVeiculos = async () => {
    if (!veiculos?.length) return;

    // Mantém a seleção múltipla separada do react-select.
    let selecionados = veiculosSelecionados.map((item) => item.value);

    const escaparHTML = (valor) =>
      String(valor ?? "").replace(/[&<>"']/g, (char) => ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[char]);

    const renderizarLista = (busca = "") => {
      const termo = busca.trim().toLowerCase();

      const filtrados = veiculos
        .filter((veiculo) => {
          const placa = String(veiculo.label ?? "").toLowerCase();
          const nome = String(
            veiculo.nome ??
            veiculo.descricao ??
            veiculo.modelo ??
            ""
          ).toLowerCase();

          return placa.includes(termo) || nome.includes(termo);
        })
        .sort((a, b) => {
          const aSelecionado = selecionados.includes(a.value);
          const bSelecionado = selecionados.includes(b.value);

          if (aSelecionado && !bSelecionado) return -1;
          if (!aSelecionado && bSelecionado) return 1;

          return 0;
        });

      return `
      <div class="swal-veiculos-conteudo">
        <div class="swal-veiculos-topo">
          <span>Veículos selecionados</span>
          <strong id="swal-veiculos-contador">
            ${selecionados.length} / ${veiculos.length}
          </strong>
        </div>

        <input
          id="swal-veiculos-busca"
          class="swal-veiculos-busca"
          placeholder="Buscar por placa ou nome..."
          autocomplete="off"
        />

        <div class="swal-veiculos-acoes">
          <button type="button" id="swal-veiculos-marcar">
            Marcar todos
          </button>

          <button type="button" id="swal-veiculos-limpar">
            Limpar seleção
          </button>
        </div>

        <div id="swal-veiculos-lista" class="swal-veiculos-lista">
          ${filtrados.map((veiculo) => {
        const id = escaparHTML(veiculo.value);
        const placa = escaparHTML(veiculo.label);
        const nome = escaparHTML(
          veiculo.nome ??
          veiculo.descricao ??
          veiculo.modelo ??
          ""
        );

        const checked = selecionados.includes(veiculo.value);

        return `
              <label class="swal-veiculo-item">
                <input
                  type="checkbox"
                  value="${id}"
                  ${checked ? "checked" : ""}
                />

                <span class="swal-veiculo-dados">
                  <strong>${placa}</strong>
                  ${nome ? `<small>${nome}</small>` : ""}
                </span>
              </label>
            `;
      }).join("")}
        </div>

        <p class="swal-veiculos-ajuda">
          Selecione os veículos que deseja incluir no relatório.
        </p>
      </div>
    `;
    };

    const resultado = await Swal.fire({
      title: "Selecionar veículos",
      html: renderizarLista(),
      width: 700,
      background: "#0b1622",
      color: "#eaf0f8",
      showCancelButton: true,
      confirmButtonText: "Confirmar seleção",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#159bd0",
      cancelButtonColor: "#26394a",
      focusConfirm: false,
      customClass: {
        popup: "swal-veiculos-modal",
        title: "swal-veiculos-titulo",
        htmlContainer: "swal-veiculos-html",
        confirmButton: "swal-veiculos-confirmar",
        cancelButton: "swal-veiculos-cancelar",
      },

      didOpen: () => {
        const popup = Swal.getPopup();
        const busca = popup.querySelector("#swal-veiculos-busca");
        const lista = popup.querySelector("#swal-veiculos-lista");
        const contador = popup.querySelector("#swal-veiculos-contador");
        const botaoMarcar = popup.querySelector("#swal-veiculos-marcar");
        const botaoLimpar = popup.querySelector("#swal-veiculos-limpar");

        const atualizarContador = () => {
          contador.textContent =
            `${selecionados.length} / ${veiculos.length}`;

          botaoMarcar.textContent =
            veiculos.every((v) => selecionados.includes(v.value))
              ? "Desmarcar todos"
              : "Marcar todos";
        };

        //=================================
        // FUNÇÃO LISTA DE EXIBIÇÃO VEICULOS
        //===================================
        const renderizarItens = () => {
          const termo = busca.value.trim().toLowerCase();

          const filtrados = veiculos
            .filter((veiculo) => {
              const placa = String(veiculo.label ?? "").toLowerCase();
              const nome = String(
                veiculo.nome ??
                veiculo.descricao ??
                veiculo.modelo ??
                ""
              ).toLowerCase();

              return placa.includes(termo) || nome.includes(termo);
            })
            .sort((a, b) => {
              const aSelecionado = selecionados.includes(a.value);
              const bSelecionado = selecionados.includes(b.value);

              // Selecionados aparecem primeiro
              if (aSelecionado && !bSelecionado) return -1;
              if (!aSelecionado && bSelecionado) return 1;

              return 0;
            });

          lista.innerHTML = filtrados.map((veiculo) => {
            const id = escaparHTML(veiculo.value);
            const placa = escaparHTML(veiculo.label);
            const nome = escaparHTML(
              veiculo.nome ??
              veiculo.descricao ??
              veiculo.modelo ??
              ""
            );

            return `
            <label class="swal-veiculo-item">
              <input
                type="checkbox"
                value="${id}"
                ${selecionados.includes(veiculo.value) ? "checked" : ""}
              />
              <span class="swal-veiculo-dados">
                <strong>${placa}</strong>
                ${nome ? `<small>${nome}</small>` : ""}
              </span>
            </label>
          `;
          }).join("") || `
          <div class="swal-veiculos-vazio">
            Nenhum veículo encontrado.
          </div>
        `;
        };

        lista.addEventListener("change", (event) => {
          const checkbox = event.target;

          if (checkbox.type !== "checkbox") return;

          const valor = veiculos.find(
            (veiculo) => String(veiculo.value) === checkbox.value
          )?.value;

          if (valor === undefined) return;

          if (checkbox.checked) {
            if (!selecionados.includes(valor)) {
              selecionados.push(valor);
            }
          } else {
            selecionados = selecionados.filter(
              (item) => item !== valor
            );
          }

          atualizarContador();
        });

        busca.addEventListener("input", renderizarItens);

        botaoMarcar.addEventListener("click", () => {
          const todosMarcados = veiculos.every(
            (veiculo) => selecionados.includes(veiculo.value)
          );

          selecionados = todosMarcados
            ? []
            : veiculos.map((veiculo) => veiculo.value);

          renderizarItens();
          atualizarContador();
        });

        botaoLimpar.addEventListener("click", () => {
          selecionados = [];
          renderizarItens();
          atualizarContador();
        });

        atualizarContador();
        busca.focus();
      },

      preConfirm: () => {
        return selecionados;
      },
    });

    if (!resultado.isConfirmed) return;

    const novosSelecionados = veiculos.filter((veiculo) =>
      resultado.value.includes(veiculo.value)
    );

    setVeiculosSelecionados(novosSelecionados);

    // Um grupo e uma seleção de veículos não ficam ativos ao mesmo tempo.
    if (novosSelecionados.length > 0) {
      setVeiculoSelecionado(null);
      setGrupoSelecionado(null);
    }
  };






  const abrirSelecaoGrupos = async () => {
    if (!grupos?.length) return;

    let selecionados = gruposSelecionados.map((item) => item.value);

    const escaparHTML = (valor) =>
      String(valor ?? "").replace(/[&<>"']/g, (char) => ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[char]);

    const renderizarItens = (busca = "") => {
      const termo = busca.trim().toLowerCase();

      const filtrados = grupos
        .filter((grupo) =>
          String(grupo.label ?? "").toLowerCase().includes(termo)
        )
        .sort((a, b) => {
          const aSelecionado = selecionados.includes(a.value);
          const bSelecionado = selecionados.includes(b.value);

          if (aSelecionado && !bSelecionado) return -1;
          if (!aSelecionado && bSelecionado) return 1;

          return 0;
        });

      return filtrados.map((grupo) => `
      <label class="swal-veiculo-item">
        <input
          type="checkbox"
          value="${escaparHTML(grupo.value)}"
          ${selecionados.includes(grupo.value) ? "checked" : ""}
        />
        <span class="swal-veiculo-dados">
          <strong>${escaparHTML(grupo.label)}</strong>
        </span>
      </label>
    `).join("") || `
      <div class="swal-veiculos-vazio">
        Nenhum grupo encontrado.
      </div>
    `;
    };

    const resultado = await Swal.fire({
      title: "Selecionar grupos",
      html: `
      <div class="swal-veiculos-conteudo">
        <div class="swal-veiculos-topo">
          <span>Grupos selecionados</span>
          <strong id="swal-grupos-contador">
            ${selecionados.length} / ${grupos.length}
          </strong>
        </div>

        <input
          id="swal-grupos-busca"
          class="swal-veiculos-busca"
          placeholder="Buscar por nome do grupo..."
          autocomplete="off"
        />

        <div class="swal-veiculos-acoes">
          <button type="button" id="swal-grupos-marcar">
            Marcar todos
          </button>
          <button type="button" id="swal-grupos-limpar">
            Limpar seleção
          </button>
        </div>

        <div id="swal-grupos-lista" class="swal-veiculos-lista">
          ${renderizarItens()}
        </div>

        <p class="swal-veiculos-ajuda">
          Selecione os grupos que deseja incluir no relatório.
        </p>
      </div>
    `,
      width: 700,
      background: "#0b1622",
      color: "#eaf0f8",
      showCancelButton: true,
      confirmButtonText: "Confirmar seleção",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#159bd0",
      cancelButtonColor: "#26394a",
      focusConfirm: false,
      customClass: {
        popup: "swal-veiculos-modal",
        title: "swal-veiculos-titulo",
        htmlContainer: "swal-veiculos-html",
        confirmButton: "swal-veiculos-confirmar",
        cancelButton: "swal-veiculos-cancelar",
      },

      didOpen: () => {
        const popup = Swal.getPopup();
        const busca = popup.querySelector("#swal-grupos-busca");
        const lista = popup.querySelector("#swal-grupos-lista");
        const contador = popup.querySelector("#swal-grupos-contador");
        const botaoMarcar = popup.querySelector("#swal-grupos-marcar");
        const botaoLimpar = popup.querySelector("#swal-grupos-limpar");

        const atualizarContador = () => {
          contador.textContent = `${selecionados.length} / ${grupos.length}`;

          botaoMarcar.textContent = grupos.every(
            (grupo) => selecionados.includes(grupo.value)
          )
            ? "Desmarcar todos"
            : "Marcar todos";
        };

        const atualizarLista = () => {
          lista.innerHTML = renderizarItens(busca.value);
        };

        lista.addEventListener("change", (event) => {
          const checkbox = event.target;

          if (checkbox.type !== "checkbox") return;

          const grupo = grupos.find(
            (item) => String(item.value) === checkbox.value
          );

          if (!grupo) return;

          if (checkbox.checked) {
            if (!selecionados.includes(grupo.value)) {
              selecionados.push(grupo.value);
            }
          } else {
            selecionados = selecionados.filter(
              (value) => value !== grupo.value
            );
          }

          atualizarLista();
          atualizarContador();
        });

        busca.addEventListener("input", atualizarLista);

        botaoMarcar.addEventListener("click", () => {
          const todosMarcados = grupos.every(
            (grupo) => selecionados.includes(grupo.value)
          );

          selecionados = todosMarcados
            ? []
            : grupos.map((grupo) => grupo.value);

          atualizarLista();
          atualizarContador();
        });

        botaoLimpar.addEventListener("click", () => {
          selecionados = [];
          atualizarLista();
          atualizarContador();
        });

        atualizarContador();
        busca.focus();
      },

      preConfirm: () => selecionados,
    });

    if (!resultado.isConfirmed) return;

    const novosSelecionados = grupos.filter((grupo) =>
      resultado.value.includes(grupo.value)
    );

    setGruposSelecionados(novosSelecionados);

    if (novosSelecionados.length > 0) {
      setGrupoSelecionado(null);
      setVeiculoSelecionado(null);
      setVeiculosSelecionados([]);
    }
  };


  // ==========================================================
  // RENDERIZAÇÃO
  // ==========================================================

  return (
    <main className="notificacao-relatorio">
      <div className="notificacao-relatorio-shell">

        {/* CABEÇALHO */}

        <header className="notificacao-relatorio-header">
          <div className="notificacao-relatorio-heading">
            <div className="notificacao-relatorio-heading-icon">
              <FaBell />
            </div>

            <div>
              <span className="notificacao-relatorio-eyebrow">
                INOVA RASTREAMENTO
              </span>

              <h1>Relatório de Notificações</h1>

              <p>
                Consulte alertas, ocorrências e finalizações da frota.
              </p>
            </div>
          </div>

          <button
            type="button"
            className="notificacao-relatorio-btn-refresh"
            onClick={atualizarOpcoes}
            disabled={carregandoVeiculos || carregandoGrupos}
            title="Atualizar veículos e grupos"
          >
            <FaSyncAlt
              className={
                carregandoVeiculos || carregandoGrupos
                  ? "notificacao-spin"
                  : ""
              }
            />
          </button>
        </header>

        {/* FILTROS */}

        <section className="notificacao-relatorio-card">
          <div className="notificacao-relatorio-section-heading">
            <span className="notificacao-relatorio-step">01</span>

            <div>
              <h2>Filtros do relatório</h2>
              <p>
                Selecione o tipo de alerta e os veículos que deseja consultar.
              </p>
            </div>
          </div>

          <div className="notificacao-relatorio-fields">

            {/* TIPO */}

            <div className="notificacao-relatorio-field notificacao-relatorio-field-full">
              <label>Tipo de alerta</label>

              <Select
                isSearchable
                isClearable={false}
                options={TIPOS_ALERTA}
                value={tipoSelecionado}
                onChange={(opcao) => {
                  setTipoSelecionado(opcao || TIPOS_ALERTA[0]);
                }}
                placeholder="Selecione o tipo de alerta"
                noOptionsMessage={() => "Nenhum tipo encontrado"}
                styles={selectStyles}
                menuPortalTarget={document.body}
                classNamePrefix="react-select"
              />
            </div>

            {/* VEÍCULO */}


            <div className="notificacao-relatorio-field">
              <label>Placa / veículo</label>

              <div className="notificacao-veiculos-container">
                <Select
                  isClearable
                  isSearchable
                  isLoading={carregandoVeiculos}
                  isDisabled={
                    veiculosSelecionados.length > 0 ||
                    gruposSelecionados.length > 0
                  }
                  options={veiculos}
                  value={veiculoSelecionado}
                  onChange={(valor) => {
                    setVeiculoSelecionado(valor);

                    if (valor) {
                      setGrupoSelecionado(null);
                      setVeiculosSelecionados([]);
                    }
                  }}
                  formatOptionLabel={(veiculo) => (
                    <div className="notificacao-veiculo-option">
                      <strong>{veiculo.placa}</strong>
                      {veiculo.nome && <small>{veiculo.nome}</small>}
                    </div>
                  )}
                  placeholder={
                    gruposSelecionados.length > 0
                      ? "Limpe a seleção de grupos para selecionar veículos"
                      : veiculosSelecionados.length > 0
                        ? `${veiculosSelecionados.length} veículo(s) selecionado(s) no modal`
                        : "Buscar por placa ou nome"
                  }
                  noOptionsMessage={() =>
                    erroVeiculos
                      ? "Falha ao carregar veículos"
                      : "Nenhum veículo encontrado"
                  }
                  loadingMessage={() => "Carregando veículos..."}
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  classNamePrefix="react-select"
                />

                <button
                  type="button"
                  className="notificacao-veiculos-selecionar"
                  disabled={
                    carregandoVeiculos ||
                    !veiculos.length ||
                    gruposSelecionados.length > 0
                  }
                  onClick={abrirSelecaoVeiculos}
                >
                  Selecionar veículos
                </button>
              </div>

              {erroVeiculos && (
                <button
                  type="button"
                  className="notificacao-relatorio-retry"
                  onClick={carregarVeiculos}
                >
                  Tentar carregar novamente
                </button>
              )}
            </div>

            {/* GRUPO */}

            <div className="notificacao-relatorio-field">
              <label>Grupo</label>

              <div className="notificacao-veiculos-container">
                <Select
                  isClearable
                  isSearchable
                  isLoading={carregandoGrupos}
                  isDisabled={
                    Boolean(veiculoSelecionado) ||
                    veiculosSelecionados.length > 0 ||
                    gruposSelecionados.length > 0
                  }
                  options={grupos}
                  value={grupoSelecionado}
                  onChange={(valor) => {
                    setGrupoSelecionado(valor);

                    if (valor) {
                      setVeiculoSelecionado(null);
                      setVeiculosSelecionados([]);
                      setGruposSelecionados([]);
                    }
                  }}
                  placeholder={
                    gruposSelecionados.length > 0
                      ? `${gruposSelecionados.length} grupo(s) selecionado(s) no modal`
                      : veiculoSelecionado || veiculosSelecionados.length > 0
                        ? "Limpe a seleção de veículos para escolher um grupo"
                        : "Buscar por grupo"
                  }
                  noOptionsMessage={() =>
                    erroGrupos
                      ? "Falha ao carregar grupos"
                      : "Nenhum grupo encontrado"
                  }
                  loadingMessage={() => "Carregando grupos..."}
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  classNamePrefix="react-select"
                />

                <button
                  type="button"
                  className="notificacao-veiculos-selecionar"
                  disabled={
                    carregandoGrupos ||
                    !grupos.length ||
                    Boolean(veiculoSelecionado) ||
                    veiculosSelecionados.length > 0
                  }
                  onClick={abrirSelecaoGrupos}
                >
                  Selecionar grupos
                </button>
              </div>

              {erroGrupos && (
                <button
                  type="button"
                  className="notificacao-relatorio-retry"
                  onClick={carregarGrupos}
                >
                  Tentar carregar novamente
                </button>
              )}
            </div>
          </div>

          {/* PERÍODO */}

          <div className="notificacao-relatorio-section-heading notificacao-relatorio-period-heading">
            <span className="notificacao-relatorio-step">02</span>

            <div>
              <h2>Período da consulta</h2>
              <p>Defina a data e o horário inicial e final.</p>
            </div>
          </div>

          <div className="notificacao-relatorio-fields notificacao-relatorio-period-fields">
            <div className="notificacao-relatorio-field">
              <label>Data inicial</label>

              <DatePicker
                selected={
                  dataInicial
                    ? new Date(`${dataInicial}T12:00:00`)
                    : null
                }
                onChange={(data) => {
                  setDataInicial(
                    data
                      ? `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}-${String(data.getDate()).padStart(2, "0")}`
                      : ""
                  );
                }}
                selectsStart
                startDate={
                  dataInicial ? new Date(`${dataInicial}T12:00:00`) : null
                }
                endDate={
                  dataFinal ? new Date(`${dataFinal}T12:00:00`) : null
                }
                maxDate={
                  dataFinal ? new Date(`${dataFinal}T12:00:00`) : null
                }
                dateFormat="dd/MM/yyyy"
                locale={ptBR}
                placeholderText="Selecione a data inicial"
                showPopperArrow={false}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                yearDropdownItemNumber={15}
                scrollableYearDropdown
                autoComplete="off"
                className="notificacao-datepicker-input"
              />
            </div>

            <div className="notificacao-relatorio-field">
              <label>Hora inicial</label>

              <input
                type="time"
                value={horaInicial}
                onChange={(event) => setHoraInicial(event.target.value)}
              />
            </div>

            <div className="notificacao-relatorio-field">
              <label>Data final</label>
              <DatePicker
                selected={
                  dataFinal
                    ? new Date(`${dataFinal}T12:00:00`)
                    : null
                }
                onChange={(data) => {
                  setDataFinal(
                    data
                      ? `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}-${String(data.getDate()).padStart(2, "0")}`
                      : ""
                  );
                }}
                selectsEnd
                startDate={
                  dataInicial ? new Date(`${dataInicial}T12:00:00`) : null
                }
                endDate={
                  dataFinal ? new Date(`${dataFinal}T12:00:00`) : null
                }
                minDate={
                  dataInicial ? new Date(`${dataInicial}T12:00:00`) : null
                }
                dateFormat="dd/MM/yyyy"
                locale={ptBR}
                placeholderText="Selecione a data final"
                showPopperArrow={false}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                yearDropdownItemNumber={15}
                scrollableYearDropdown
                autoComplete="off"
                className="notificacao-datepicker-input"
              />
            </div>

            <div className="notificacao-relatorio-field">
              <label>Hora final</label>

              <input
                type="time"
                value={horaFinal}
                onChange={(event) => setHoraFinal(event.target.value)}
              />
            </div>
          </div>

          {/* AÇÕES */}

          <div className="notificacao-relatorio-actions">
            <div className="notificacao-relatorio-actions-description">
              <strong>Ações do relatório</strong>
              <span>
                Consulte ou exporte os dados encontrados.
              </span>
            </div>

            <div className="notificacao-relatorio-actions-buttons">
              <button
                type="button"
                className="notificacao-relatorio-btn notificacao-relatorio-btn-excel"
                onClick={exportarExcel}
                disabled={!registrosFiltrados.length}
              >
                <RiFileExcel2Line />
                Excel
              </button>

              <button
                type="button"
                className="notificacao-relatorio-btn notificacao-relatorio-btn-pdf"
                onClick={gerarPDF}
                disabled={!registrosFiltrados.length}
              >
                <FaFilePdf />
                PDF
              </button>

              <button
  type="button"
  className="notificacao-relatorio-btn notificacao-relatorio-btn-consultar"
  onClick={consultar}
  disabled={
    consultando ||
    (
      !veiculoSelecionado &&
      veiculosSelecionados.length === 0 &&
      !grupoSelecionado &&
      gruposSelecionados.length === 0
    )
  }
>
  {consultando ? (
    <span className="notificacao-relatorio-button-spinner" />
  ) : (
    <FaSearch />
  )}

  {consultando ? "Consultando..." : "Consultar"}
</button>

              <button
                type="button"
                className="notificacao-relatorio-btn notificacao-relatorio-btn-limpar"
                onClick={limparFiltros}
                disabled={consultando}
              >
                Limpar
              </button>
            </div>
          </div>
        </section>

        {/* RESULTADOS */}

        <section className="notificacao-relatorio-results">
          <div className="notificacao-relatorio-results-header">
            <div className="notificacao-relatorio-section-heading">
              <span className="notificacao-relatorio-step">03</span>

              <div className="notificacao-relatorio-resumo">
                <div className="notificacao-relatorio-resumo-info">
                  <h2>Registros encontrados</h2>

                  <p>
                    {registrosFiltrados.length} registro(s) para os filtros atuais.
                  </p>
                </div>

                <div className="notificacao-relatorio-resumo-status">
                  <div className="notificacao-relatorio-resumo-item finalizados">
                    <span className="notificacao-relatorio-resumo-indicador">
                      <FaCheck />
                    </span>

                    <div>
                      <strong>
                        {registrosFiltrados.filter((item) => item.finalizado_em).length}
                      </strong>
                      <span>Finalizados</span>
                    </div>
                  </div>

                  <div className="notificacao-relatorio-resumo-item pendentes">
                    <span className="notificacao-relatorio-resumo-indicador">
                      •
                    </span>

                    <div>
                      <strong>
                        {registrosFiltrados.filter((item) => !item.finalizado_em).length}
                      </strong>
                      <span>Pendentes</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="notificacao-relatorio-search">
              <FaSearch />

              <input
                type="search"
                placeholder="Buscar nos resultados..."
                value={pesquisa}
                onChange={(event) => {
                  setPesquisa(event.target.value);
                  setPagina(1);
                }}
              />
            </div>
          </div>

          {/* TABELA */}

          <div className="notificacao-relatorio-table-wrapper">
            <table className="notificacao-relatorio-table">
              <thead>
                <tr>
                  <th>Placa</th>
                  <th>Descrição</th>
                  <th>Ocorrido em</th>
                  <th>Motorista</th>
                  <th>Tipo</th>
                  <th>Endereço</th>
                  <th>Notificação</th>
                  <th>Finalizado em</th>
                  <th>Finalizado por</th>
                  <th>Comentário</th>
                  <th aria-label="Status">
                    Status
                  </th>
                </tr>
              </thead>

              <tbody>
                {consultando ? (
                  <tr>
                    <td colSpan={11} className="notificacao-relatorio-empty">
                      <span className="notificacao-relatorio-button-spinner" />
                      <strong>Consultando notificações...</strong>
                      <span>Aguarde enquanto buscamos os registros.</span>
                    </td>
                  </tr>
                ) : registrosPagina.length > 0 ? (
                  registrosPagina.map((item, index) => {
                    const finalizado = Boolean(item.finalizado_em);

                    return (
                      <tr key={item.id ?? `${item.placa}-${index}`}>
                        <td>
                          <span className="notificacao-relatorio-plate">
                            {texto(item.placa)}
                          </span>
                        </td>

                        <td>{texto(item.descricao)}</td>

                        <td>{formatarDataHora(item.posicao_em)}</td>

                        <td>{texto(item.motora ?? item.motorista)}</td>

                        <td>
                          <span className="notificacao-relatorio-type">
                            {texto(item.tipo)}
                          </span>
                        </td>

                        <td className="notificacao-relatorio-address">
                          {texto(item.endereco)}
                        </td>

                        <td className="notificacao-relatorio-message">
                          {texto(item.mensagem)}
                        </td>

                        <td>{formatarDataHora(item.finalizado_em)}</td>

                        <td>
                          {item.finalizado_por
                            ? item.finalizado_por.charAt(0).toLocaleUpperCase("pt-BR") +
                            item.finalizado_por.slice(1)
                            : "-"}
                        </td>

                        <td className="notificacao-relatorio-comment">
                          {texto(item.obs)}
                        </td>

                        <td>
                          <span
                            className={
                              "notificacao-relatorio-status " +
                              (finalizado
                                ? "notificacao-relatorio-status-done"
                                : "notificacao-relatorio-status-pending")
                            }
                            title={finalizado ? "Finalizado" : "Pendente"}
                          >
                            {finalizado ? <FaCheck /> : "•"}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={11} className="notificacao-relatorio-empty">
                      <div className="notificacao-relatorio-empty-icon">
                        <FaRegClipboard />
                      </div>

                      <strong>
                        {consultaRealizada
                          ? "Nenhum registro encontrado"
                          : "Nenhuma consulta realizada"}
                      </strong>

                      <span>
                        {consultaRealizada
                          ? "Tente alterar os filtros para encontrar notificações."
                          : "Selecione os filtros e clique em Consultar."}
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINAÇÃO */}

          <footer className="notificacao-relatorio-pagination">
            <div className="notificacao-relatorio-page-size">
              <label htmlFor="notificacao-page-size">
                Linhas por página
              </label>

              <select
                id="notificacao-page-size"
                value={porPagina}
                onChange={(event) => {
                  setPorPagina(Number(event.target.value));
                  setPagina(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <span className="notificacao-relatorio-pagination-count">
              {registrosFiltrados.length
                ? `${(paginaAtual - 1) * porPagina + 1}–${Math.min(
                  paginaAtual * porPagina,
                  registrosFiltrados.length
                )} de ${registrosFiltrados.length}`
                : "0–0 de 0"}
            </span>

            <div className="notificacao-relatorio-pagination-buttons">
              <button
                type="button"
                onClick={() =>
                  setPagina((atual) => Math.max(1, atual - 1))
                }
                disabled={paginaAtual <= 1}
                aria-label="Página anterior"
              >
                ‹
              </button>

              <span>
                {paginaAtual} / {totalPaginas}
              </span>

              <button
                type="button"
                onClick={() =>
                  setPagina((atual) =>
                    Math.min(totalPaginas, atual + 1)
                  )
                }
                disabled={paginaAtual >= totalPaginas}
                aria-label="Próxima página"
              >
                ›
              </button>
            </div>
          </footer>
        </section>
      </div>
    </main>
  );
}

